import React, { useMemo, useState } from 'react'
import { isMobileOnly } from 'react-device-detect'
import PropTypes from 'prop-types'

import { usePathname } from 'next/navigation'

import SwiperCore, { Navigation, Pagination } from 'swiper/core'
import { Swiper, SwiperSlide } from 'swiper/react'

import { BLOCKCHAIN, CTO } from 'Constants/Paths'
import {
  SLIDE_SPACE_BETWEEN,
  SLIDE_SPEED,
  SLIDE_WIDTH,
  SWIPER_KINDS,
} from 'Constants/Swipers'

import { Container, Content, Gradient } from './styles'

import Bullets from '../Bullets'
import CtoSlide from '../CtoSlide'
import Nav from '../Navigation'
import ProjectSlide from '../ProjectSlide'
import TestimonialSlide from '../TestimonialSlide'

// Scoped here rather than in the root layout: as a layout import this was
// the only render-blocking stylesheet on every route, swiper page or not.
import 'swiper/swiper-bundle.css'

// eslint-disable-next-line react-hooks/rules-of-hooks
SwiperCore.use([Navigation, Pagination])

function SwiperWide({
  data = [],
  slidesPerView = 3,
  kind = SWIPER_KINDS.PROJECTS,
}) {
  const speed = SLIDE_SPEED
  const [swiper, setSwiper] = useState(null)

  const pathname = usePathname()
  const secondary = pathname === BLOCKCHAIN || pathname === CTO

  const contentWidth = useMemo(() => {
    if (kind === SWIPER_KINDS.PROJECTS || kind === SWIPER_KINDS.CTOS) {
      return [
        0,
        (SLIDE_WIDTH.medium + SLIDE_SPACE_BETWEEN.medium) * slidesPerView,
        (SLIDE_WIDTH.medium + SLIDE_SPACE_BETWEEN.medium) * slidesPerView,
        SLIDE_WIDTH.wide * slidesPerView +
          SLIDE_SPACE_BETWEEN.wide * slidesPerView,
      ]
    }
    return [1]
  }, [kind, slidesPerView])

  const needGradient =
    kind === SWIPER_KINDS.PROJECTS || kind === SWIPER_KINDS.CTOS

  const handleClickPrev = () => {
    swiper.slidePrev(speed)
  }

  const handleClickNext = () => {
    swiper.slideNext(speed)
  }

  if (!data) return null

  return (
    <Container>
      <Content width={contentWidth}>
        {needGradient && <Gradient />}

        <Swiper
          autoHeight={!isMobileOnly && kind === SWIPER_KINDS.TESTIMONIALS}
          centeredSlides
          grabCursor
          loop
          pagination={{
            el: `.pagination__container--${kind}`,
            bulletClass: secondary
              ? 'blueprint-swiper__bullet-pink'
              : 'blueprint-swiper__bullet',
            bulletActiveClass: secondary
              ? 'blueprint-swiper__bullet-pink--active'
              : 'blueprint-swiper__bullet--active',
            bulletElement: 'div',
            clickable: true,
          }}
          slidesPerView={slidesPerView}
          spaceBetween={kind === SWIPER_KINDS.TESTIMONIALS ? 10 : 0}
          speed={speed}
          onSwiper={slider => setSwiper(slider)}
        >
          {data.map(item => (
            <SwiperSlide key={`${item.id}slideWide`}>
              {kind === SWIPER_KINDS.PROJECTS && (
                <ProjectSlide project={item} />
              )}

              {kind === SWIPER_KINDS.TESTIMONIALS && (
                <TestimonialSlide data={item} />
              )}

              {kind === SWIPER_KINDS.CTOS && <CtoSlide person={item} />}
            </SwiperSlide>
          ))}
        </Swiper>

        {needGradient && <Gradient isRight />}

        <Nav
          handlerClickNext={handleClickNext}
          handlerClickPrev={handleClickPrev}
          offset={kind === SWIPER_KINDS.TESTIMONIALS}
          secondary={secondary}
        />
      </Content>

      <Bullets modifier={kind} />
    </Container>
  )
}

SwiperWide.propTypes = {
  data: PropTypes.array,
  kind: PropTypes.oneOf([
    SWIPER_KINDS.PROJECTS,
    SWIPER_KINDS.TESTIMONIALS,
    SWIPER_KINDS.CTOS,
  ]),
  slidesPerView: PropTypes.number,
}
export default SwiperWide
